import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mainNav, secondaryNav, visibleNav, type NavItem } from './navItems';
import { initials, useAuth } from '@/lib/auth';
import { usePerms } from '@/lib/perms';

interface Props {
  onNavigate?: () => void;
}

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] transition-colors ${
          isActive
            ? 'bg-primary-50 font-semibold text-primary-800'
            : 'font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full transition-opacity ${
              isActive ? 'bg-primary-500 opacity-100' : 'opacity-0'
            }`}
          />
          <i className={`${item.icon} text-[16px] ${isActive ? 'text-primary-600' : 'text-foreground-400'}`} />
          <span className="flex-1 truncate">{t(item.labelKey)}</span>
          {item.badge && (
            <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-800">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ onNavigate }: Props) {
  const { t } = useTranslation();
  const { me, flags, logout } = useAuth();
  const { can } = usePerms();
  const roleLabel: Record<string, string> = {
    admin: t('set.roleAdmin'),
    partner_manager: t('set.rolePartner'),
    project_manager: t('set.roleProject'),
    finance: t('set.roleFinance'),
    viewer: t('set.roleViewer'),
  };

  return (
    <aside className="flex h-full w-[248px] shrink-0 flex-col border-r border-background-200 bg-background-50">
      <div className="flex h-14 items-center gap-2.5 border-b border-background-200 px-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
          <i className="ri-plant-line text-[17px]" />
        </span>
        <div className="leading-tight">
          <p className="text-[13px] font-semibold tracking-tight text-foreground-950">Treejar</p>
          <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-foreground-400">{t('shell.hub')}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2.5 py-4 thin-scroll">
        <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
          {t('nav.operations')}
        </p>
        <nav className="flex flex-col gap-0.5">
          {visibleNav(mainNav, flags, (a) => can(a)).map((item) => (
            <NavRow key={item.path} item={item} onNavigate={onNavigate} />
          ))}
        </nav>

        <p className="mb-1.5 mt-6 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
          {t('nav.channel')}
        </p>
        <nav className="flex flex-col gap-0.5">
          {visibleNav(secondaryNav, flags, (a) => can(a)).map((item) => (
            <NavRow key={item.path} item={item} onNavigate={onNavigate} />
          ))}
        </nav>

        <div className="mt-6 rounded-md border border-background-200 bg-background-100 p-3">
          <div className="flex items-center gap-1.5">
            <i className="ri-alert-line text-[13px] text-accent-600" />
            <p className="text-[11px] font-semibold text-foreground-900">{t('shell.staleTitle', { count: 2 })}</p>
          </div>
          <p className="mt-1 text-[11px] leading-4 text-foreground-500">{t('shell.staleBody')}</p>
          <NavLink
            to="/partners"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700 hover:text-primary-800"
          >
            {t('shell.reviewPartners')} <i className="ri-arrow-right-line text-[11px]" />
          </NavLink>
        </div>
      </div>

      <div className="border-t border-background-200 p-3">
        <NavLink
          to="/portal/login"
          className="mb-2 flex items-center gap-1.5 rounded-md border border-primary-200 bg-primary-50 px-2 py-1.5 text-[11px] font-semibold text-primary-800 transition-colors hover:bg-primary-100"
        >
          <i className="ri-external-link-line text-[12px]" />
          {t('portal.brand')}
        </NavLink>
        <div className="flex items-center gap-2.5 rounded-md px-1 py-1.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-200 text-[11px] font-semibold text-secondary-900">
            {initials(me?.name ?? '')}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[12px] font-semibold text-foreground-950">{me?.name}</p>
            <p className="truncate text-[10px] text-foreground-500">{(me && roleLabel[me.role]) || t('shell.admin')}</p>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            aria-label={t('auth.signOut')}
            title={t('auth.signOut')}
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-700"
          >
            <i className="ri-logout-box-r-line text-[14px]" />
          </button>
        </div>
      </div>
    </aside>
  );
}